
import React, { useState, useEffect, useMemo } from 'react';
import { User, UserRole, UserStatus, ERPModule, LoginLog, Country } from '../types';
import { apiService } from '../services/apiService';
import { 
  Search, UserPlus, Shield, Activity, Edit2, Trash2, UserCheck, 
  X, Check, ShieldCheck, Globe, History, CheckSquare, Square, 
  Loader2, Eye, EyeOff, Info, Lock, 
  Fingerprint, MapPin, ChevronDown,
  Briefcase, User as UserIcon, CheckCircle2, UserX, Database
} from 'lucide-react';

interface UserModuleProps {
  currentUser: User;
}

const UserModule: React.FC<UserModuleProps> = ({ currentUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [logs, setLogs] = useState<LoginLog[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'logs'>('users');
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [viewingLogUser, setViewingLogUser] = useState<User | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const [modalTab, setModalTab] = useState<'profile' | 'access'>('profile');

  const [formData, setFormData] = useState({
    username: '',
    fullName: '',
    designation: '',
    password: '',
    role: 'Read Only' as UserRole,
    status: 'active' as UserStatus,
    allowedModules: [ERPModule.DASHBOARD] as ERPModule[],
    homeCountry: Country.TANZANIA as Country,
    allowedCountries: [Country.TANZANIA] as Country[]
  });

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    setIsLoading(true);
    try {
        const [userData, logData] = await Promise.all([
            apiService.getUsers(),
            apiService.getLoginLogs()
        ]);
        setUsers(Array.isArray(userData) ? userData : []);
        setLogs(Array.isArray(logData) ? logData : []);
    } catch (e) {
        console.error("Registry Sync Error:", e);
        setUsers([]);
    } finally {
        setIsLoading(false);
    }
  };

  const applyPreset = (preset: 'admin' | 'staff' | 'global') => {
      let modules: ERPModule[] = [ERPModule.DASHBOARD];
      let countries: Country[] = [formData.homeCountry];
      let role: UserRole = 'Read Only';
      
      switch (preset) {
          case 'admin':
              modules = Object.values(ERPModule);
              countries = Object.values(Country);
              role = 'Admin';
              break;
          case 'staff':
              modules = [ERPModule.DASHBOARD, ERPModule.TASKS, ERPModule.ORDERS_PIPELINE, ERPModule.CRM];
              role = 'Add/Edit';
              break;
          case 'global':
              countries = Object.values(Country);
              break;
      }
      setFormData(prev => ({ 
        ...prev, 
        role: preset !== 'global' ? role : prev.role,
        allowedModules: preset === 'global' ? prev.allowedModules : modules, 
        allowedCountries: countries 
      }));
  };

  const handleOpenAdd = () => {
    setEditingUser(null);
    setModalTab('profile');
    setShowPassword(false);
    setFormData({ 
      username: '', 
      fullName: '', 
      designation: '',
      password: '', 
      role: 'Read Only', 
      status: 'active',
      allowedModules: [ERPModule.DASHBOARD],
      homeCountry: Country.TANZANIA,
      allowedCountries: [Country.TANZANIA]
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (user: User) => {
    setEditingUser(user);
    setModalTab('profile');
    setShowPassword(false);
    setFormData({
      username: user.username,
      fullName: user.fullName,
      designation: user.designation || '',
      password: user.password || '',
      role: user.role,
      status: user.status || 'active',
      allowedModules: user.allowedModules || [ERPModule.DASHBOARD],
      homeCountry: user.homeCountry || Country.TANZANIA,
      allowedCountries: user.allowedCountries && user.allowedCountries.length > 0 ? user.allowedCountries : [user.homeCountry]
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (id === currentUser.id) {
        alert("Security Override: You cannot delete your own root account.");
        return;
    }
    if (window.confirm('Are you sure you want to revoke all access and delete this user? This action will be audited.')) {
      await apiService.deleteUser(id);
      await loadData();
    }
  };

  const toggleModule = (module: ERPModule) => {
    setFormData(prev => {
        const current = [...prev.allowedModules];
        if (current.includes(module)) {
            if (module === ERPModule.DASHBOARD) return prev; 
            return { ...prev, allowedModules: current.filter(m => m !== module) };
        } else {
            return { ...prev, allowedModules: [...current, module] };
        }
    });
  };

  const toggleCountry = (country: Country) => {
      setFormData(prev => {
          const current = [...prev.allowedCountries];
          if (current.includes(country)) {
              if (country === prev.homeCountry) return prev;
              return { ...prev, allowedCountries: current.filter(c => c !== country) };
          } else {
              return { ...prev, allowedCountries: [...current, country] };
          }
      });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.username || !formData.password || !formData.fullName) {
        alert("Verification Error: All credentials fields are mandatory.");
        return;
    }

    setIsSaving(true);
    try {
        const payload: User = {
          id: editingUser ? editingUser.id : `u-${crypto.randomUUID()}`,
          ...formData,
          avatarUrl: editingUser?.avatarUrl || `https://api.dicebear.com/7.x/avataaars/svg?seed=${formData.username}`
        };
        
        const success = await apiService.saveUser(payload);
        if (success) {
            setIsModalOpen(false);
            await loadData();
        }
    } catch (error: any) {
        console.error("User Save Error:", error);
        alert(`Critical Error: ${error.message}`);
    } finally {
        setIsSaving(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.fullName.toLowerCase().includes(searchTerm.toLowerCase()) || 
    u.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadge = (role: UserRole) => {
    if (role === 'Admin') return 'bg-rose-100 text-rose-700 border-rose-200';
    if (role === 'Add/Edit') return 'bg-indigo-100 text-indigo-700 border-indigo-200';
    if (role === 'Read Only') return 'bg-slate-100 text-slate-700 border-slate-200';
    return 'bg-slate-50 text-slate-400 border-slate-100';
  };

  return (
    <div className="p-8 space-y-8 animate-in fade-in duration-500 h-full flex flex-col">
      <header className="flex flex-col md:flex-row md:items-end justify-between gap-4 shrink-0">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <h2 className="text-3xl font-black text-slate-800 tracking-tight uppercase">Identity Registry</h2>
            <div className="bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full flex items-center gap-2">
                <ShieldCheck size={14} className="text-indigo-600" />
                <span className="text-[10px] font-black text-indigo-700 uppercase tracking-widest">Root Access Only</span>
            </div>
          </div>
          <p className="text-slate-500 font-medium">Provisioning personnel accounts and multi-branch authorizations.</p>
        </div>
        <div className="flex gap-3">
          <div className="bg-white p-1 rounded-2xl border border-slate-200 shadow-sm flex">
            <button onClick={() => setActiveTab('users')} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === 'users' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-700'}`}>
              <UserCheck size={14} /> Personnel
            </button>
            <button onClick={() => setActiveTab('logs')} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-bold transition-all ${activeTab === 'logs' ? 'bg-indigo-600 text-white shadow-lg' : 'text-slate-500 hover:text-slate-700'}`}>
              <Activity size={14} /> Audit Trail
            </button>
          </div>
          {activeTab === 'users' && (
            <button onClick={handleOpenAdd} className="bg-indigo-600 text-white px-6 py-2.5 rounded-2xl font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center gap-2">
              <UserPlus size={18} /> Register Identity
            </button>
          )}
        </div>
      </header>

      <div className="flex-1 flex flex-col min-h-0">
      {isLoading ? (
        <div className="flex-1 flex flex-col items-center justify-center text-slate-400 gap-4">
            <Loader2 className="animate-spin" size={32} />
            <p className="text-[10px] font-black uppercase tracking-[0.2em]">Synchronizing Registry...</p>
        </div>
      ) : activeTab === 'users' ? (
        <div className="space-y-6 flex flex-col h-full">
           <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-sm flex items-center justify-between shrink-0">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                <input 
                  type="text"
                  placeholder="Find personnel by name or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 bg-slate-50 border border-slate-100 rounded-2xl text-sm focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                />
              </div>
              <div className="hidden md:flex items-center gap-4 px-4 text-slate-400 text-[10px] font-black uppercase tracking-widest">
                  <Database size={16} /> {users.length} Records Detected
              </div>
           </div>

           <div className="bg-white rounded-[40px] border border-slate-200 shadow-sm overflow-hidden flex-1 flex flex-col">
             {users.length > 0 ? (
               <div className="overflow-y-auto custom-scrollbar flex-1">
                <table className="min-w-full divide-y divide-slate-100">
                <thead className="bg-slate-50/50 sticky top-0 z-10">
                    <tr className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <th className="px-8 py-5">Personnel Identity</th>
                    <th className="px-8 py-5">Access Level</th>
                    <th className="px-8 py-5">Branch Authorization</th>
                    <th className="px-8 py-5">Modules</th>
                    <th className="px-8 py-5 text-right">Ops</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                    {filteredUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-indigo-50/10 transition-colors group">
                        <td className="px-8 py-5">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-2xl flex items-center justify-center font-bold border overflow-hidden shadow-sm bg-indigo-50 text-indigo-700">
                            {user.avatarUrl ? <img src={user.avatarUrl} alt="" className="w-full h-full object-cover" /> : user.fullName.charAt(0)}
                            </div>
                            <div>
                            <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{user.fullName}</p>
                            <p className="text-[10px] text-slate-400 font-bold uppercase mt-0.5">@{user.username}</p>
                            </div>
                        </div>
                        </td>
                        <td className="px-8 py-5">
                        <span className={`px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${getRoleBadge(user.role)}`}>
                            {user.role}
                        </span>
                        </td>
                        <td className="px-8 py-5">
                            <div className="flex flex-wrap gap-1">
                                {user.allowedCountries?.map(c => (
                                    <span key={c} className="px-2 py-0.5 rounded-lg bg-slate-50 border border-slate-100 text-[8px] font-black text-slate-500 uppercase">{c.substring(0, 2)}</span>
                                ))}
                            </div>
                        </td>
                        <td className="px-8 py-5">
                            <div className="text-[10px] font-bold text-slate-500">
                                {user.allowedModules?.length} Modules Active
                            </div>
                        </td>
                        <td className="px-8 py-5 text-right">
                        <div className="flex justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button onClick={() => { setViewingLogUser(user); setIsHistoryModalOpen(true); }} className="p-2 text-slate-400 hover:text-amber-600 transition-all"><History size={18} /></button>
                            <button onClick={() => handleOpenEdit(user)} className="p-2 text-slate-400 hover:text-indigo-600 transition-all"><Edit2 size={18} /></button>
                            <button onClick={() => handleDelete(user.id)} className="p-2 text-slate-400 hover:text-rose-600 transition-all"><Trash2 size={18} /></button>
                        </div>
                        </td>
                    </tr>
                    ))}
                </tbody>
                </table>
               </div>
             ) : (
                <div className="flex-1 flex flex-col items-center justify-center text-slate-300 gap-4">
                    <UserX size={48} className="opacity-10" />
                    <p className="text-xs font-black uppercase tracking-widest">No users found in database.</p>
                    <button onClick={loadData} className="text-indigo-600 text-[10px] font-black uppercase hover:underline">Sync Registry</button>
                </div>
             )}
           </div>
        </div>
      ) : (
        <div className="bg-white rounded-[40px] border border-slate-200 shadow-sm overflow-hidden flex-1">
            <div className="overflow-y-auto h-full custom-scrollbar">
                <table className="min-w-full divide-y divide-slate-100">
                    <thead className="bg-slate-50 sticky top-0">
                        <tr className="text-left text-[10px] font-black text-slate-400 uppercase tracking-widest">
                            <th className="px-8 py-5">Entity</th>
                            <th className="px-8 py-5">Origin IP</th>
                            <th className="px-8 py-5">Timestamp</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                        {logs.map((log) => (
                            <tr key={log.id} className="hover:bg-slate-50 transition-colors">
                                <td className="px-8 py-4">
                                    <div className="flex items-center gap-3">
                                        <span className="text-sm font-bold text-slate-800 uppercase">{log.fullName}</span>
                                        <span className="text-xs text-slate-400">@{log.username}</span>
                                    </div>
                                </td>
                                <td className="px-8 py-4 font-mono text-xs text-slate-500">{log.ipAddress}</td>
                                <td className="px-8 py-4 text-xs text-slate-500 font-bold">{new Date(log.loginTime).toLocaleString()}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
      )}
      </div>

      {/* Edit/Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-4xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-100">
             <header className="p-8 bg-indigo-600 text-white flex justify-between items-center shrink-0">
                <div className="flex items-center gap-4">
                   <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center backdrop-blur-md">
                      {editingUser ? <Edit2 size={24} /> : <UserPlus size={24} />}
                   </div>
                   <div>
                        <h3 className="text-xl font-black uppercase tracking-tight">{editingUser ? 'Profile Redefinition' : 'Identity Registry'}</h3>
                        <p className="text-indigo-100 text-[10px] font-black uppercase tracking-widest mt-1">Operational Node Access Management</p>
                   </div>
                </div>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-white/10 rounded-xl transition-colors"><X size={24} /></button>
             </header>
             
             <div className="flex bg-slate-50 border-b border-slate-200 px-8 shrink-0">
                <button onClick={() => setModalTab('profile')} className={`px-6 py-5 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${modalTab === 'profile' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400'}`}>
                    <UserCheck size={14} /> Profile Essentials
                </button>
                <button onClick={() => setModalTab('access')} className={`px-6 py-5 text-[10px] font-black uppercase tracking-widest border-b-2 transition-all flex items-center gap-2 ${modalTab === 'access' ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-slate-400'}`}>
                    <Lock size={14} /> Authorization Matrix
                </button>
             </div>

             <form onSubmit={handleSubmit} className="p-8 space-y-8 overflow-y-auto custom-scrollbar flex-1 bg-white">
                {modalTab === 'profile' ? (
                  <div className="space-y-8 animate-in slide-in-from-left-4 duration-300">
                    <div className="grid grid-cols-2 gap-8">
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                           <UserIcon size={12} className="text-indigo-500" /> Full Identity Name
                        </label>
                        <input required type="text" value={formData.fullName} onChange={e => setFormData({...formData, fullName: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-black text-slate-900 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all uppercase" />
                      </div>
                      <div className="space-y-3">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1 flex items-center gap-2">
                           <Briefcase size={12} className="text-slate-300" /> Operational Designation
                        </label>
                        <input type="text" value={formData.designation} onChange={e => setFormData({...formData, designation: e.target.value})} className="w-full px-6 py-4 bg-slate-50 border border-slate-200 rounded-3xl text-sm font-bold text-slate-700 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all" />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="p-6 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6">
                            <h4 className="text-[10px] font-black text-indigo-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <Fingerprint size={14} /> Authentication
                            </h4>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-tighter ml-1">Username</label>
                                    <input required type="text" value={formData.username} onChange={e => setFormData({...formData, username: e.target.value.toLowerCase()})} className="w-full px-5 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-mono focus:ring-4 focus:ring-indigo-500/10 outline-none" />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-tighter ml-1">Security Key</label>
                                    <div className="relative">
                                        <input required type={showPassword ? "text" : "password"} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} className="w-full px-5 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-mono focus:ring-4 focus:ring-indigo-500/10 outline-none pr-12" />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-300 hover:text-indigo-600 transition-colors">
                                            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="p-6 bg-slate-50 rounded-[2.5rem] border border-slate-100 space-y-6">
                            <h4 className="text-[10px] font-black text-emerald-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                                <MapPin size={14} /> Deployment
                            </h4>
                            <div className="space-y-4">
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-tighter ml-1">Primary Node (Home Branch)</label>
                                    <select value={formData.homeCountry} onChange={e => setFormData({...formData, homeCountry: e.target.value as Country})} className="w-full px-5 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-black uppercase tracking-widest outline-none appearance-none cursor-pointer">
                                        {Object.values(Country).map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[9px] font-black text-slate-400 uppercase tracking-tighter ml-1">Personnel Role</label>
                                    <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value as UserRole})} className="w-full px-5 py-3 bg-white border border-slate-200 rounded-2xl text-xs font-black uppercase tracking-widest outline-none appearance-none cursor-pointer">
                                        <option value="Read Only">Read Only</option>
                                        <option value="Add/Edit">Add/Edit</option>
                                        <option value="Admin">Admin</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-8 animate-in slide-in-from-right-4 duration-300">
                    <div className="bg-slate-900 p-8 rounded-[3rem] border border-white/5 relative overflow-hidden shadow-2xl">
                        <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-3xl -mr-20 -mt-20"></div>
                        <div className="relative z-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
                            <div>
                                <h4 className="text-white font-black text-lg uppercase tracking-tight flex items-center gap-3">
                                    <Shield size={20} className="text-indigo-400" />
                                    Clearance Presets
                                </h4>
                                <p className="text-slate-500 text-[10px] font-bold uppercase tracking-widest mt-2">Rapid Authorization Template Application</p>
                            </div>
                            <div className="flex flex-wrap gap-2">
                                <button type="button" onClick={() => applyPreset('admin')} className="px-5 py-2.5 text-[10px] font-black uppercase tracking-widest bg-white text-slate-900 rounded-2xl hover:bg-indigo-500 hover:text-white transition-all shadow-xl">Full Registry Admin</button>
                                <button type="button" onClick={() => applyPreset('staff')} className="px-5 py-2.5 text-[10px] font-black uppercase tracking-widest bg-white/5 text-white border border-white/10 rounded-2xl hover:bg-white/10 transition-all">Regional Staff (Add/Edit)</button>
                                <button type="button" onClick={() => applyPreset('global')} className="px-5 py-2.5 text-[10px] font-black uppercase tracking-widest bg-white/5 text-white border border-white/10 rounded-2xl hover:bg-white/10 transition-all">Audit Mode (Read Only)</button>
                            </div>
                        </div>
                    </div>
                    
                    <div className="space-y-6">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-2 flex items-center gap-2">
                           <Globe size={14} className="text-emerald-500" /> Branch Authorization (Multi-Node Access)
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.values(Country).map(country => (
                                <button 
                                    key={country} 
                                    type="button" 
                                    disabled={formData.role === 'Admin'}
                                    onClick={() => toggleCountry(country)} 
                                    className={`flex items-center gap-4 p-5 rounded-[2rem] border transition-all text-left relative overflow-hidden group ${formData.role === 'Admin' || formData.allowedCountries.includes(country) ? 'bg-emerald-50 border-emerald-200 text-emerald-700 shadow-md ring-4 ring-emerald-500/5' : 'bg-white border-slate-200 text-slate-400 hover:border-emerald-300'}`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 ${formData.role === 'Admin' || formData.allowedCountries.includes(country) ? 'bg-emerald-500 text-white shadow-lg shadow-emerald-200' : 'bg-slate-50 text-slate-300'}`}>
                                        {formData.role === 'Admin' || formData.allowedCountries.includes(country) ? <CheckSquare size={18} /> : <Square size={18} />}
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-[11px] font-black uppercase block tracking-tight truncate">{country} Node</span>
                                        <span className="text-[9px] font-bold uppercase opacity-60">
                                            {formData.role === 'Admin' || formData.allowedCountries.includes(country) ? 'Authorized' : 'Restricted'}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-4">
                        <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] ml-2">Module Selective Access</h4>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {Object.values(ERPModule).map(module => (
                                <button 
                                    key={module} 
                                    type="button" 
                                    disabled={formData.role === 'Admin'}
                                    onClick={() => toggleModule(module)} 
                                    className={`flex items-center gap-4 p-5 rounded-[2rem] border transition-all text-left relative overflow-hidden group ${formData.role === 'Admin' || formData.allowedModules.includes(module) ? 'bg-indigo-50 border-indigo-200 text-indigo-700 shadow-md ring-4 ring-indigo-500/5' : 'bg-white border-slate-200 text-slate-400 hover:border-indigo-300'}`}
                                >
                                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 transition-transform group-hover:scale-110 ${formData.role === 'Admin' || formData.allowedModules.includes(module) ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-200' : 'bg-slate-50 text-slate-300'}`}>
                                        {formData.role === 'Admin' || formData.allowedModules.includes(module) ? <CheckSquare size={18} /> : <Square size={18} />}
                                    </div>
                                    <div className="min-w-0">
                                        <span className="text-[11px] font-black uppercase block tracking-tight truncate">{module.replace('_', ' ')}</span>
                                        <span className="text-[9px] font-bold uppercase opacity-60">
                                            {formData.role === 'Admin' || formData.allowedModules.includes(module) ? 'Authorized' : 'Disabled'}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </div>
                  </div>
                )}
             </form>

             <footer className="p-8 bg-slate-50 border-t border-slate-100 flex justify-between items-center shrink-0">
                <div className="flex items-center gap-3 text-slate-400">
                    <Info size={16} />
                    <span className="text-[9px] font-black uppercase tracking-widest">Changes are synced to local secure registry</span>
                </div>
                <div className="flex gap-4">
                    <button type="button" disabled={isSaving} onClick={() => setIsModalOpen(false)} className="px-8 py-3 text-slate-500 font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 rounded-2xl transition-all disabled:opacity-50">Discard</button>
                    <button onClick={handleSubmit} disabled={isSaving} className="px-12 py-3 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 hover:bg-indigo-700 transition-all active:scale-95 flex items-center gap-2 disabled:opacity-50">
                        {isSaving ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
                        {isSaving ? 'Synchronizing...' : 'Commit Profile'}
                    </button>
                </div>
             </footer>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserModule;
